// select-spot — interaction primitive (ASK-129 ruling 2, the `featured-comic` cutover).
//
// "Look at the featured item and press E to inspect it." Declarative, and deliberately so: the item
// on a `single-spot` fixture is seated by the fixture's own socket through `stage._seatTableSocket`,
// whose `featured-hero` branch already calls `_wireSeatedComicInteraction` and registers the mesh as
// a **wall-comic** — the shipped interactable kind that owns hover outline and the E press for a
// comic standing face-out. Adding a second handler here would give one mesh two owners competing
// for the same press, which is the defect class this codebase has now paid for three times.
//
// So this primitive publishes the manifest's declaration onto the composed entry (the same
// migration-window shape `hero-pickup` uses) and wires nothing. That is not a stub: the preflight
// requires every declared interaction to be registered before the fixture may compose at all, and
// what this one has to say is *"the seated item is the interactable, at the trigger the manifest
// names"* — which is true, and which the render-truth gate asserts by driving a real E press.
//
// 🟡 MANIFEST MISMATCH, MEASURED — reported to Content, not worked around here.
// `featured-comic.manifest.json` declares `target: "showcase_origin"`. The catalogue GLB
// (`featured-comic.glb @ 759b8ce`) carries exactly two nodes — `Body` (mesh, y 0.3) and
// `SOCKET_SHOWCASE` (empty, y 0.4). There is no `showcase_origin`. Nothing downstream resolves the
// field today, so it is inert rather than wrong-by-consequence; it is named here so the next reader
// does not go looking for the node, and `target` is passed through verbatim rather than
// "corrected" to the socket id — a consumer inventing an authored name is how the two halves drift.

import { registerInteraction } from "./index.js";

function wireSelectSpot(ctx) {
	const { interactionDef, composedEntry } = ctx;
	if (!composedEntry) return;

	composedEntry.selectSpot = {
		enabled: true,
		trigger: interactionDef?.trigger || "E",
		// Verbatim from the manifest — see the mismatch note above.
		target: interactionDef?.target ?? null,
		// Where the press is actually handled, so a reader of the composed entry can find the owner
		// rather than concluding nothing handles it.
		handledBy: "wall-comic (registered by _wireSeatedComicInteraction on the socket-seated mesh)",
	};

	// No dispose closure: this primitive holds no listener, no timer and no scene object. Returning
	// one that did nothing would be a lie the teardown path has to keep calling.
}

registerInteraction("select-spot", wireSelectSpot);

export { wireSelectSpot };
